import * as React from "react";
import {useMemo, useRef, useState} from "react";
import {Alert, Chip, IconButton, InputAdornment, Stack, Tooltip} from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import {useTranslation} from "react-i18next";
import Property from "crqlar-react-common/dist/components/Property";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {CasablancaConfig} from "crqlar-react-common/dist/models/integrations/casablanca/casablancaConfig.model";
import {
    PmsProvider,
    PmsRateMappingConfig,
    PmsRateMappingEntry
} from "crqlar-react-common/dist/models/integrations/pmsRateMapping.model";
import {CasablancaService} from "crqlar-react-common/dist/services/integrations/casablanca/casablanca.service";

interface Props {
    casablancaConfig: CasablancaConfig;
    onChange: (rateMappingConfig: PmsRateMappingConfig) => void;
}

interface ImportSummary {
    added: number;
    updated: number;
    skipped: number;
}

/**
 * Canonical form used for duplicate detection and CSV merging. Mirrors
 * `PmsRateMappingResolver.normalize` on the backend — uppercased, and stripped of the non-breaking
 * spaces and zero-width characters that Excel exports drag along.
 */
const normalizeCode = (rawCode?: string): string =>
    (rawCode || '')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        // \s does not cover NBSP (U+00A0), which Excel exports carry.
        .replace(/[\s\u00A0]+/g, '')
        .toUpperCase();

/**
 * Minimal RFC-4180 reader — enough for a Casablanca *Ratenmanagement* export saved as CSV, and not
 * worth a dependency. Handles a UTF-8 BOM, `;` (German Excel), `,` or tab delimiters, quoted fields
 * with doubled quotes inside them, and CRLF line endings.
 */
const parseDelimitedText = (text: string): string[][] => {
    const body = text.replace(/^\uFEFF/, '');

    // Pick the delimiter that appears most often outside quotes on the first line.
    const firstLine = body.split(/\r?\n/, 1)[0] || '';
    const outsideQuotes = firstLine.replace(/"[^"]*"/g, '');
    const delimiter = [';', ',', '\t']
        .map(candidate => ({candidate, count: outsideQuotes.split(candidate).length - 1}))
        .sort((a, b) => b.count - a.count)[0].candidate;

    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < body.length; i++) {
        const char = body[i];

        if (inQuotes) {
            if (char === '"') {
                if (body[i + 1] === '"') {
                    field += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                field += char;
            }
            continue;
        }

        if (char === '"') {
            inQuotes = true;
        } else if (char === delimiter) {
            row.push(field);
            field = '';
        } else if (char === '\n') {
            row.push(field);
            rows.push(row);
            row = [];
            field = '';
        } else if (char !== '\r') {
            field += char;
        }
    }

    row.push(field);
    rows.push(row);

    return rows
        .map(cells => cells.map(cell => cell.trim()))
        .filter(cells => cells.some(cell => cell.length > 0));
};

const headerKey = (cell: string) => cell.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Works out which columns of the export hold the raw code, the short label and the long
 * designation. Casablanca's *Ratenmanagement* export ships German headers, but CX also hand-builds
 * files, so an unrecognised header falls back to positional order (code, label, description).
 */
const resolveColumns = (headerCells: string[]) => {
    const keys = headerCells.map(headerKey);
    const indexOfAny = (candidates: string[]) => keys.findIndex(key => candidates.indexOf(key) >= 0);

    const codeIndex = indexOfAny(['mappingcode', 'mappingcodes', 'code', 'ratecode', 'ratenkennung', 'kennung']);
    const shortIndex = indexOfAny(['kurzbezeichnung', 'kurzbez', 'kurzname', 'shortname', 'shortcode', 'label']);
    const longIndex = indexOfAny(['name', 'bezeichnung', 'langbezeichnung', 'beschreibung', 'description']);

    // Nothing recognisable → there is no header row, so treat every line as data.
    if (codeIndex < 0 && shortIndex < 0 && longIndex < 0) {
        return {codeIndex: 0, labelIndex: 1, descriptionIndex: 2, hasHeader: false};
    }

    return {
        codeIndex: codeIndex >= 0 ? codeIndex : 0,
        // The ticket's columns are MappingCode + Kurzbezeichnung/Name: prefer the short designation
        // as the label staff see, and keep the long one as the description.
        labelIndex: shortIndex >= 0 ? shortIndex : longIndex,
        descriptionIndex: shortIndex >= 0 ? longIndex : -1,
        hasHeader: true
    };
};

/**
 * Rate & meal-plan mapping grid for a Casablanca property.
 *
 * Casablanca streams opaque, system-generated rate strings (`PMS_QCHC623G`) on the OTA feed. This
 * table translates them into the short board codes staff read on the arrival lists and the floor
 * plan ("HP", "ÜF", "AI"), so the hotel gets readable codes **without** paying for the Casablanca
 * POS API endpoint. Anything left unmapped keeps showing the raw string — nothing is ever lost.
 *
 * The mappings are saved with the rest of the connector config, which is already scoped to the
 * organization, so one property's table can never be read by another hotel.
 */
const CasablancaRateMappingForm = ({casablancaConfig, onChange}: Props) => {

    const {t} = useTranslation();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [filter, setFilter] = useState('');
    const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
    const [importError, setImportError] = useState<string | null>(null);
    const [discovering, setDiscovering] = useState(false);
    const [discoverResult, setDiscoverResult] = useState<string | null>(null);

    const mappings: PmsRateMappingEntry[] = useMemo(
        () => casablancaConfig.rateMappingConfig?.mappings || [],
        [casablancaConfig.rateMappingConfig]
    );

    //region Handlers

    const commit = (nextMappings: PmsRateMappingEntry[]) => {
        onChange({
            pmsProvider: casablancaConfig.rateMappingConfig?.pmsProvider || PmsProvider.CASABLANCA,
            mappings: nextMappings
        });
    };

    const updateEntry = (index: number, field: keyof PmsRateMappingEntry, value: string) => {
        commit(mappings.map((entry, i) => i === index ? {...entry, [field]: value} : entry));
    };

    const removeEntry = (index: number) => {
        commit(mappings.filter((_, i) => i !== index));
    };

    const addEntry = () => {
        // Prepend so the new row is visible straight away even on a several-hundred-row table, and
        // drop the filter that would otherwise hide it. The backend re-sorts the table on save.
        setFilter('');
        commit([{rawCode: '', mappedLabel: '', description: ''}, ...mappings]);
    };

    /**
     * Merges a parsed export into the existing table: a row whose code is already mapped is
     * overwritten (the export is the source of truth), everything else is appended. Rows without
     * both a code and a label are skipped rather than half-saved.
     */
    const mergeImported = (imported: PmsRateMappingEntry[]): ImportSummary => {
        const next = [...mappings];
        const indexByCode = new Map<string, number>();
        next.forEach((entry, index) => indexByCode.set(normalizeCode(entry.rawCode), index));

        const summary: ImportSummary = {added: 0, updated: 0, skipped: 0};

        imported.forEach(entry => {
            const code = normalizeCode(entry.rawCode);
            if (!code || !entry.mappedLabel) {
                summary.skipped++;
                return;
            }

            const existingIndex = indexByCode.get(code);
            if (existingIndex !== undefined) {
                next[existingIndex] = {...next[existingIndex], ...entry};
                summary.updated++;
            } else {
                indexByCode.set(code, next.length);
                next.push(entry);
                summary.added++;
            }
        });

        commit(next);
        return summary;
    };

    const handleFile = async (file: File) => {
        setImportSummary(null);
        setImportError(null);

        // .xlsx/.xls are ZIP containers, not text — reading them here would mean a parser
        // dependency for a file the user can re-save as CSV in two clicks.
        if (/\.xlsx?$/i.test(file.name)) {
            setImportError(t('Excel files cannot be read directly. In Excel choose "Save as" and pick CSV, then upload that file.') as string);
            return;
        }

        try {
            const rows = parseDelimitedText(await file.text());
            if (rows.length === 0) {
                setImportError(t('The file contains no rows') as string);
                return;
            }

            const columns = resolveColumns(rows[0]);
            const dataRows = columns.hasHeader ? rows.slice(1) : rows;

            const imported: PmsRateMappingEntry[] = dataRows.map(cells => ({
                rawCode: (cells[columns.codeIndex] || '').trim(),
                mappedLabel: (columns.labelIndex >= 0 ? cells[columns.labelIndex] || '' : '').trim(),
                description: (columns.descriptionIndex >= 0 ? cells[columns.descriptionIndex] || '' : '').trim() || undefined
            }));

            const summary = mergeImported(imported);
            setImportSummary(summary);
            if (summary.added === 0 && summary.updated === 0) {
                setImportError(t('No mappings could be read from the file. Expected a column with the mapping code and one with the short designation.') as string);
            }
        } catch (e: any) {
            setImportError(e?.message || t('Failed to read the file') as string);
        }
    };

    /**
     * Pulls the rate codes the connector has actually received and prepends the ones that carry no
     * label yet, so CX gets a concrete worklist instead of an empty grid of opaque strings.
     */
    const discoverCodes = async () => {
        if (!casablancaConfig.id) return;
        setDiscovering(true);
        setDiscoverResult(null);
        setImportError(null);
        try {
            const codes = await CasablancaService.discoverRateCodes(casablancaConfig.id);
            const known = new Set(mappings.map(entry => normalizeCode(entry.rawCode)));
            const missing = codes.filter(code => !known.has(normalizeCode(code.rawCode)));

            if (missing.length > 0) {
                setFilter('');
                commit([
                    ...missing.map(code => ({rawCode: code.rawCode, mappedLabel: '', description: ''})),
                    ...mappings
                ]);
            }

            setDiscoverResult(
                t('Found {{total}} rate codes, {{missing}} of them not mapped yet', {
                    total: codes.length,
                    missing: missing.length
                }) as string
            );
        } catch (e: any) {
            setImportError(e?.response?.data?.message || e?.message || t('Failed to load rate codes') as string);
        } finally {
            setDiscovering(false);
        }
    };

    //endregion Handlers

    //region Derived state

    /** Codes listed more than once — the backend keeps only the last, so warn before that happens. */
    const duplicateCodes = useMemo(() => {
        const seen = new Set<string>();
        const duplicates = new Set<string>();
        mappings.forEach(entry => {
            const code = normalizeCode(entry.rawCode);
            if (!code) return;
            if (seen.has(code)) duplicates.add(code);
            seen.add(code);
        });
        return duplicates;
    }, [mappings]);

    const visibleRows = useMemo(() => {
        const needle = filter.trim().toLowerCase();
        return mappings
            .map((entry, index) => ({entry, index}))
            .filter(({entry}) => !needle || [entry.rawCode, entry.mappedLabel, entry.description]
                .some(value => (value || '').toLowerCase().indexOf(needle) >= 0));
    }, [mappings, filter]);

    const unmappedCount = mappings.filter(entry => !entry.mappedLabel?.trim()).length;

    //endregion Derived state

    //region UI

    const toolbar =
        <Stack direction={'row'} gap={1} flexWrap={'wrap'} alignItems={'center'} width={'100%'}>
            <TextField
                size={'small'}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t('Filter by code or label') as string}
                sx={{flex: '1 1 220px'}}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position={'start'}>
                            <SearchIcon fontSize={'small'}/>
                        </InputAdornment>
                    )
                }}
            />

            <Button variant={'outlined'} onClick={addEntry}>
                {t('Add mapping')}
            </Button>

            <Button variant={'outlined'} onClick={() => fileInputRef.current?.click()}>
                {t('Import CSV')}
            </Button>

            <Tooltip title={casablancaConfig.id ? '' : t('Save the configuration first') as string}>
                <span>
                    <Button
                        variant={'outlined'}
                        onClick={discoverCodes}
                        disabled={!casablancaConfig.id || discovering}
                        startIcon={discovering ? <CircularProgress size={16}/> : undefined}
                    >
                        {t('Load codes from imported reservations')}
                    </Button>
                </span>
            </Tooltip>

            <input
                ref={fileInputRef}
                type={'file'}
                accept={'.csv,.txt,.tsv,text/csv,text/plain'}
                hidden
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFile(file);
                    // Reset so picking the same file twice still fires a change event.
                    e.target.value = '';
                }}
            />
        </Stack>;

    const statusChips =
        <Stack direction={'row'} gap={1} flexWrap={'wrap'} alignItems={'center'}>
            <Chip size={'small'} label={t('Mappings: {{total}}', {total: mappings.length})}/>
            {
                unmappedCount > 0 &&
                <Chip
                    size={'small'}
                    color={'warning'}
                    variant={'outlined'}
                    label={t('Without a label: {{total}}', {total: unmappedCount})}/>
            }
            {
                duplicateCodes.size > 0 &&
                <Chip
                    size={'small'}
                    color={'error'}
                    variant={'outlined'}
                    label={t('Duplicate codes: {{total}}', {total: duplicateCodes.size})}/>
            }
        </Stack>;

    const columnHeader =
        <Stack direction={'row'} gap={1} width={'100%'} sx={{pr: '40px'}}>
            <Typography variant={'caption'} sx={{width: '32%'}}>{t('Raw code (from Casablanca)')}</Typography>
            <Typography variant={'caption'} sx={{width: '22%'}}>{t('Display label')}</Typography>
            <Typography variant={'caption'} sx={{width: '46%'}}>{t('Description (optional)')}</Typography>
        </Stack>;

    const rows = visibleRows.map(({entry, index}) => {
        const isDuplicate = duplicateCodes.has(normalizeCode(entry.rawCode));
        return (
            <Stack direction={'row'} key={index} width={'100%'} gap={1}>
                <TextField
                    value={entry.rawCode || ''}
                    onChange={(e) => updateEntry(index, 'rawCode', e.target.value)}
                    variant={'outlined'}
                    size={'small'}
                    sx={{width: '32%'}}
                    error={isDuplicate}
                    helperText={isDuplicate ? t('Duplicate code — only the last row is kept') : undefined}
                    placeholder={'PMS_QCHC623G'}
                />
                <TextField
                    value={entry.mappedLabel || ''}
                    onChange={(e) => updateEntry(index, 'mappedLabel', e.target.value)}
                    variant={'outlined'}
                    size={'small'}
                    sx={{width: '22%'}}
                    placeholder={'HP'}
                />
                <TextField
                    value={entry.description || ''}
                    onChange={(e) => updateEntry(index, 'description', e.target.value)}
                    variant={'outlined'}
                    size={'small'}
                    sx={{width: '46%'}}
                    placeholder={t('Halbpension Longstay') as string}
                />
                <IconButton onClick={() => removeEntry(index)}>
                    <DeleteIcon sx={{
                        width: '24px',
                        height: '24px',
                        color: CommonThemeColors.sys.error.main
                    }}/>
                </IconButton>
            </Stack>
        );
    });

    //endregion UI

    return (
        <AdditionalProperties title={t('Rate & meal plan mapping')} variant={'outlined'}>
            <Property
                title={t('Rate code to display label')}
                tooltip={t('Translates the rate codes Casablanca generates into the short board codes shown on arrival lists and the floor plan.')}
            >
                <Stack gap={1} width={'100%'}>
                    <Alert
                        variant={'outlined'}
                        severity={'info'}
                        sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                    >
                        {t('Casablanca sends generated rate codes such as PMS_QCHC623G. Map them to the short board code your staff reads (HP, ÜF, AI) to show it on arrival lists and the floor plan without the paid POS API. Take the codes from the Ratenmanagement export. Unmapped codes keep showing the original code, so nothing is lost.')}
                    </Alert>

                    {toolbar}
                    {statusChips}

                    {
                        importSummary &&
                        <Alert severity={'success'} onClose={() => setImportSummary(null)}>
                            {t('Imported: {{added}} added, {{updated}} updated, {{skipped}} skipped', {
                                added: importSummary.added,
                                updated: importSummary.updated,
                                skipped: importSummary.skipped
                            })}
                        </Alert>
                    }

                    {
                        discoverResult &&
                        <Alert severity={'info'} onClose={() => setDiscoverResult(null)}>
                            {discoverResult}
                        </Alert>
                    }

                    {
                        importError &&
                        <Alert severity={'error'} onClose={() => setImportError(null)}>
                            {importError}
                        </Alert>
                    }

                    {
                        mappings.length > 0 &&
                        <Stack gap={1} width={'100%'} mt={1}>
                            {columnHeader}
                            {rows}
                            {
                                visibleRows.length === 0 &&
                                <Typography variant={'caption'}>
                                    {t('No mapping matches the filter')}
                                </Typography>
                            }
                        </Stack>
                    }

                    {
                        mappings.length === 0 &&
                        <Alert
                            variant={'outlined'}
                            severity={'warning'}
                            sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                        >
                            {t('No mappings yet. While this table is empty the connector falls back to the Casablanca POS API for the board code.')}
                        </Alert>
                    }
                </Stack>
            </Property>
        </AdditionalProperties>
    );
};

export default CasablancaRateMappingForm;
